import { useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Checkbox, Chip, DataTable, Input } from '@/components/ui'
import type { LinePatch, PriceLetterLine } from '../api/letterTypes'
import {
  formatMoney,
  formatPct,
  oldPriceMismatch,
  pctChange,
  visibleColumns,
  type LineColumn,
} from '../utils/letter-lines'

export interface NumberCellProps {
  value: number | null
  disabled: boolean
  label: string
  onCommit: (v: number | null) => void
  className?: string
  /** Extra attributes for the input (e.g. a data-* hook for keyboard navigation). */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement> & Record<`data-${string}`, string | undefined>
}

export function NumberCell({ value, disabled, label, onCommit, className, inputProps }: NumberCellProps) {
  const [draft, setDraft] = useState(value === null ? '' : String(value))
  return (
    <Input
      {...inputProps}
      className={className}
      aria-label={label}
      inputMode="decimal"
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim() === '' ? null : Number(draft.replace(/[$,]/g, ''))
        if (next !== null && Number.isNaN(next)) return setDraft(value === null ? '' : String(value))
        if (next !== value) onCommit(next)
      }}
    />
  )
}

export interface LetterLinesGridProps {
  lines: PriceLetterLine[]
  symbol: string
  showTariff: boolean
  locked: boolean
  onPatch: (lineId: number, changes: LinePatch) => void
}

export function LetterLinesGrid({ lines, symbol, showTariff, locked, onPatch }: LetterLinesGridProps) {
  const columns = useMemo(() => {
    const defs: Record<LineColumn, ColumnDef<PriceLetterLine, unknown>> = {
      include: {
        id: 'include',
        header: 'Include',
        cell: ({ row }) => (
          <Checkbox
            aria-label={`Include ${row.original.product_name}`}
            checked={row.original.included}
            disabled={locked}
            onChange={(e) => onPatch(row.original.id, { included: e.target.checked })}
          />
        ),
      },
      product: {
        id: 'product',
        header: 'Product',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="text-role-data-primary">{row.original.product_name}</span>
            {row.original.sheet_note ? (
              <span className="text-role-metadata" title="Internal note, not printed on the letter">
                Internal note (not printed): {row.original.sheet_note}
              </span>
            ) : null}
          </div>
        ),
      },
      part: { id: 'part', header: 'Customer part #', cell: ({ row }) => row.original.customer_part_number ?? '—' },
      old: {
        id: 'old',
        header: 'Current price',
        cell: ({ row }) => (
          <NumberCell
            label={`Current price for ${row.original.product_name}`}
            value={row.original.old_price}
            disabled={locked}
            onCommit={(v) => onPatch(row.original.id, { old_price: v })}
          />
        ),
      },
      new: {
        id: 'new',
        header: 'New price',
        cell: ({ row }) => (
          <NumberCell
            label={`New price for ${row.original.product_name}`}
            value={row.original.new_price}
            disabled={locked}
            onCommit={(v) => onPatch(row.original.id, { new_price: v })}
          />
        ),
      },
      change: {
        id: 'change',
        header: 'Change',
        cell: ({ row }) => formatPct(pctChange(row.original.old_price, row.original.new_price)),
      },
      tariff_pct: {
        id: 'tariff_pct',
        header: 'Tariff %',
        cell: ({ row }) => (
          <NumberCell
            label={`Tariff % for ${row.original.product_name}`}
            value={row.original.tariff_pct}
            disabled={locked}
            onCommit={(v) => onPatch(row.original.id, { tariff_pct: v })}
          />
        ),
      },
      tariff_amount: {
        id: 'tariff_amount',
        header: 'Tariff $',
        cell: ({ row }) => (
          <NumberCell
            label={`Tariff amount for ${row.original.product_name}`}
            value={row.original.tariff_amount}
            disabled={locked}
            onCommit={(v) => onPatch(row.original.id, { tariff_amount: v })}
          />
        ),
      },
      total: {
        id: 'total',
        header: 'Total',
        cell: ({ row }) => (
          <NumberCell
            label={`Total for ${row.original.product_name}`}
            value={row.original.total_price}
            disabled={locked}
            onCommit={(v) => onPatch(row.original.id, { total_price: v })}
          />
        ),
      },
      odoo: {
        id: 'odoo',
        header: 'Odoo now',
        cell: ({ row }) =>
          row.original.odoo_current_price === null ? (
            <span className="text-role-metadata">Not on pricelist</span>
          ) : (
            <span className="flex items-center gap-1">
              {formatMoney(symbol, row.original.odoo_current_price)}
              {oldPriceMismatch(row.original) ? (
                <Chip size="xs" variant="warning">
                  differs from current
                </Chip>
              ) : null}
            </span>
          ),
      },
      kind: {
        id: 'kind',
        header: 'Prints as',
        cell: ({ row }) => (
          <select
            aria-label={`Prints as for ${row.original.product_name}`}
            value={row.original.kind}
            disabled={locked}
            onChange={(e) => onPatch(row.original.id, { kind: e.target.value as PriceLetterLine['kind'] })}
            className="h-[var(--input-height)] rounded-sm border border-border-default bg-surface px-2 text-md text-primary"
          >
            <option value="price">Price</option>
            <option value="discontinued">No longer supplied</option>
          </select>
        ),
      },
    }
    return visibleColumns(showTariff).map((c) => defs[c])
  }, [showTariff, locked, symbol, onPatch])

  return (
    <section className="flex flex-col gap-2 rounded-sm border border-border-default bg-surface p-4">
      <h2 className="text-role-section-label m-0">3. Products on the letter</h2>
      <p className="m-0 text-role-metadata">
        Untick a row to leave it off the letter. Cost, margin and forecast from the workbook are not stored.
      </p>
      <DataTable
        data={lines}
        columns={columns}
        stickyFirstColumn={false}
        getRowId={(l) => String(l.id)}
        maxHeight="60vh"
        criticalRowPredicate={(l) => !l.included}
      />
    </section>
  )
}
